import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  IconButton,
  TextField,
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Drawer,
  InputAdornment,
  Button,
  useTheme,
  styled,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Link,
  Card,
  CardContent,
} from '@mui/material';
import {
  Layers,
  Search,
  Home,
  Nature,
  Menu as MenuIcon,
  School,
  Close,
  FamilyRestroom,
  Man,
  TrendingUp,
  Public,
  ExpandMore,
  Groups,
  LocalHospital,
  Person,
  Grass,
  Spa,
  BugReport,
  Hive,
  Water as WaterIcon,
  Groups3,
  Handshake,
  Diversity3,
  House,
  Roofing,
  Map,
  Pets as LivestockIcon,
  EggAlt as PoultryIcon,
  Cottage as EmergencyHomeIcon, 
  AccessibilityNew, 
  ChildCare, 
  Terrain, 
  Engineering 
} from '@mui/icons-material';

// Constantes de style
const drawerWidth = 240;
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const backgroundColor = '#f5f5f5';

// Composants stylisés
const MainContainer = styled('div')({
  display: 'flex',
  backgroundColor: backgroundColor,
  minHeight: '100vh',
  flexDirection: 'column'
});

const MainContentWithDrawer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  marginTop: '64px',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginTop: '56px'
  }
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: backgroundColor,
  height: 'calc(100vh - 64px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    height: 'calc(100vh - 56px)'
  }
}));

const DrawerWrapper = styled('div')(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  overflow: 'auto',
  height: 'calc(100vh - 64px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    zIndex: 1300,
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '&.drawerOpen': {
      transform: 'translateX(0)',
    },
    '&.drawerClose': {
      transform: 'translateX(-100%)',
    },
  }
}));

const MobileDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: primaryColor,
  '& th': {
    color: 'white',
    fontWeight: 'bold',
    whiteSpace: 'nowrap'
  }
});

const AgeRow = styled(TableRow)({
  backgroundColor: '#e0e0e0',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#d0d0d0 !important'
  }
});

const menuItems = [
  { text: 'Ménages par type', icon: <FamilyRestroom color="primary" />, to: '/RGPHAEDataPage' },
  { text: 'Population par localisation', icon: <Public color="primary" />, to: '/PopulationDataPage' },
  { text: 'Ménages collectifs', icon: <Groups color="primary" />, to: '/PopulationCollectiveHouseholdsPage' },
  { text: 'Sans-abris', icon: <EmergencyHomeIcon color="primary" />, to: '/HomelessPopulationPage' },
  { text: 'Ménages ordinaires', icon: <Home color="primary" />, to: '/PopulationResidentPage' },
  { text: 'Sexe et masculinité', icon: <Man color="primary" />, to: '/PopulationDataPageM' },
  { text: 'Rapport de masculinité', icon: <TrendingUp color="primary" />, to: '/MasculinityRatioPage' },
  { text: 'Population par âge', icon: <ChildCare color="primary" />, to: '/PopulationDataPageAge' },
  { text: "Grands groupes d'âge", icon: <AccessibilityNew color="primary" />, to: '/PopulationAgeGroupPageGrand' },
  { text: "Groupes d'âge (éducation)", icon: <School color="primary" />, to: '/PopulationByAgeGroupPage' },
  { text: 'Groupes d\'âge (santé)', icon: <LocalHospital color="primary" />, to: '/AgeGroupPopulationPage' },
  { text: 'Adultes (18+)', icon: <Person color="primary" />, to: '/AdultPopulationPage' },
  
  // Agriculture
  { text: 'Ménages agricoles', icon: <Nature color="primary" />, to: '/AgriculturalHouseholdsPage' },
  { text: 'Pratiques agricoles', icon: <Grass color="primary" />, to: '/AgriculturalHouseholdsPages' },
  { text: 'Types de cultures', icon: <Spa color="primary" />, to: '/AgriculturalHouseholdsPage20' },
  
  // Élevage
  { text: 'Élevage par espèces', icon: <LivestockIcon color="primary" />, to: '/FarmingHouseholdsPage' },
  { text: 'Élevage de volailles', icon: <PoultryIcon color="primary" />, to: '/poultryFarmingPage' },
  { text: 'Élevage spécial', icon: <BugReport color="primary" />, to: '/insectFarmingHouseholdsPage' },
  { text: 'Répartition cheptel', icon: <LivestockIcon color="primary" />, to: '/livestockDistributionPage' },
  { text: 'Répartition volailles', icon: <PoultryIcon color="primary" />, to: '/poultryDistributionPage' },
  
  // Matériel agricole
  { text: 'Équipements agricoles', icon: <Engineering color="primary" />, to: '/agriculturalEquipmentPage' },
  { text: 'Apiculture', icon: <Hive color="primary" />, to: '/beekeepingHouseholdsPage' },
  { text: 'Pêche/aquaculture', icon: <WaterIcon color="primary" />, to: '/fishingHouseholdsPage' },
  
  // Associations
  { text: 'Interventions associations', icon: <Groups3 color="primary" />, to: '/associationInterventionPage' },
  { text: 'Interventions coopératives', icon: <Handshake color="primary" />, to: '/cooperativeInterventionPage' },
  { text: 'Interventions ONG', icon: <Diversity3 color="primary" />, to: '/interventionProjectsPage' },
  
  // Habitat
  { text: "Type d'habitat", icon: <House color="primary" />, to: '/housingDistributionPage' },
  { text: 'Matériau toit', icon: <Roofing color="primary" />, to: '/roofMaterialPage' },
  { text: 'Matériau sol', icon: <Layers color="primary" />, to: '/housingMaterialPage' },
  { text: 'Ménages agricoles', icon: <Terrain color="primary" />, to: '/agriculturalHouseholdsPageProvCom' },
  { text: 'Population par âge/résidence', icon: <Map color="primary" />, to: '/populationAgeResidencePage' },
];

// Structure des données corrigée
const ageGroupData = {
  title: "Répartition de la population des ménages ordinaires par groupe d'âges fonctionnels (santé) selon le sexe et le milieu de résidence",
  ageGroups: [
    { age: "< 1 an", total: "445,592", urbain: { total: "101,925", masculin: "51,030", feminin: "50,895" }, rural: { total: "343,667", masculin: "171,338", feminin: "172,329" } },
    { age: "< 5 ans", total: "2,041,065", urbain: { total: "457,682", masculin: "229,873", feminin: "227,809" }, rural: { total: "1,583,383", masculin: "792,997", feminin: "790,386" } },
    { age: "1 - 14 ans", total: "5,060,214", urbain: { total: "1,108,103", masculin: "549,553", feminin: "558,550" }, rural: { total: "3,952,111", masculin: "1,983,001", feminin: "1,969,110" } },
    { age: "18 - 23 mois", total: "190,100", urbain: { total: "43,104", masculin: "21,574", feminin: "21,530" }, rural: { total: "146,996", masculin: "73,476", feminin: "73,520" } },
    { age: "< 6 mois", total: "226,165", urbain: { total: "51,829", masculin: "26,023", feminin: "25,806" }, rural: { total: "174,336", masculin: "86,665", feminin: "87,671" } },
    { age: "6 - 59 mois", total: "1,814,905", urbain: { total: "405,858", masculin: "203,852", feminin: "202,006" }, rural: { total: "1,409,047", masculin: "706,327", feminin: "702,720" } },
    { age: "0 - 59 mois", total: "2,041,070", urbain: { total: "457,687", masculin: "229,875", feminin: "227,812" }, rural: { total: "1,583,383", masculin: "792,992", feminin: "790,391" } },
    { age: "10 - 19 ans", total: "2,984,549", urbain: { total: "702,425", masculin: "335,039", feminin: "367,386" }, rural: { total: "2,282,124", masculin: "1,113,534", feminin: "1,168,590" } },
    { age: "15 - 24 ans", total: "2,339,899", urbain: { total: "648,266", masculin: "306,430", feminin: "341,836" }, rural: { total: "1,691,633", masculin: "760,528", feminin: "931,105" } },
    { age: "15 - 34 ans", total: "3,878,066", urbain: { total: "1,143,444", masculin: "544,853", feminin: "598,591" }, rural: { total: "2,734,622", masculin: "1,207,699", feminin: "1,526,923" } },
    { age: "10 - 49 ans", total: "7,096,869", urbain: { total: "1,918,678", masculin: "939,774", feminin: "978,904" }, rural: { total: "5,178,191", masculin: "2,366,534", feminin: "2,811,657" } },
    { age: "60 ans ou plus", total: "687,513", urbain: { total: "106,783", masculin: "49,776", feminin: "57,007" }, rural: { total: "580,730", masculin: "262,174", feminin: "318,556" } },
    { age: "0 - 8 ans", total: "3,519,666", urbain: { total: "776,821", masculin: "390,386", feminin: "386,435" }, rural: { total: "2,742,845", masculin: "1,375,363", feminin: "1,367,482" } },
    { age: "1000 premiers jours (0-3 ans)", total: "1,647,196", urbain: { total: "371,029", masculin: "185,906", feminin: "185,123" }, rural: { total: "1,276,167", masculin: "638,728", feminin: "637,439" } },
    { age: "< 18 ans", total: "6,362,131", urbain: { total: "1,421,891", masculin: "699,680", feminin: "722,211" }, rural: { total: "4,940,240", masculin: "2,465,052", feminin: "2,475,188" } },
    { age: "18 ans ou plus", total: "5,858,803", urbain: { total: "1,568,575", masculin: "779,086", feminin: "789,489" }, rural: { total: "4,290,228", masculin: "1,879,257", feminin: "2,410,971" } }
  ],
  total: {
    total: "12,220,934",
    urbain: { total: "2,990,466", masculin: "1,478,766", feminin: "1,511,700" },
    rural: { total: "9,230,468", masculin: "4,344,309", feminin: "4,886,159" }
  }
};

const AgeGroupPopulationPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [openKeyStats, setOpenKeyStats] = useState(true);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  // Calcul des indicateurs clés
  const keyStats = {
    totalPopulation: ageGroupData.total.total,
    urbanPercentage: (
      (parseFloat(ageGroupData.total.urbain.total.replace(/,/g, '')) / 
      parseFloat(ageGroupData.total.total.replace(/,/g, ''))) * 100
    ).toFixed(1),
    ruralPercentage: (
      (parseFloat(ageGroupData.total.rural.total.replace(/,/g, '')) / 
      parseFloat(ageGroupData.total.total.replace(/,/g, ''))) * 100
    ).toFixed(1),
    largestAgeGroup: ageGroupData.ageGroups.reduce((max, group) => 
      parseFloat(group.total.replace(/,/g, '')) > parseFloat(max.total.replace(/,/g, '')) ? group : max
    ),
    highestGenderGap: ageGroupData.ageGroups.reduce((max, group) => {
      const urbanGap = Math.abs(
        parseFloat(group.urbain.masculin.replace(/,/g, '')) - 
        parseFloat(group.urbain.feminin.replace(/,/g, ''))
      );
      const ruralGap = Math.abs(
        parseFloat(group.rural.masculin.replace(/,/g, '')) - 
        parseFloat(group.rural.feminin.replace(/,/g, ''))
      );
      const maxGap = Math.max(urbanGap, ruralGap);
      return maxGap > max.gap ? { group, gap: maxGap } : max;
    }, { group: null, gap: 0 })
  };

  const filteredAgeGroups = ageGroupData.ageGroups.filter(group =>
    group.age.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Composant du menu latéral
  const drawer = (
    <div>
      {isMobile && (
        <MobileDrawerHeader>
          <IconButton onClick={handleDrawerToggle}>
            <Close color="primary" />
          </IconButton>
        </MobileDrawerHeader>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.to}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <MainContainer>
      <CssBaseline />
      <NavigationBar />
      <MainContentWithDrawer>
        {!isMobile ? (
          <DrawerWrapper>
            {drawer}
          </DrawerWrapper>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        )}

        <ContentWrapper>
          <Container maxWidth="lg">
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<MenuIcon />} 
                  onClick={handleDrawerToggle}
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Menu
                </Button>
              </Box>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3, borderLeft: `4px solid ${primaryColor}` }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
                    <Typography variant="h5" component="h2" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, sm: 0 } }}>
                      {ageGroupData.title}
                    </Typography>
                  </Box> 
                  <Grid container spacing={1} style={{ marginTop: '5px', marginBottom: '5px' }}>
                    <Grid item xs={12} sm={6} md={6}></Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        size="small"
                        placeholder="Rechercher groupe d'âge..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: { xs: '100%', sm: '480px' } }}
                      />
                    </Grid>
                  </Grid>
                  
                  {/* Section des indicateurs clés */}
                  <Accordion expanded={openKeyStats} onChange={() => setOpenKeyStats(!openKeyStats)} sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Indicateurs Clés
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Population Totale
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {ageGroupData.total.total}
                              </Typography>
                              <Typography variant="caption">
                                Ménages ordinaires
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Population Urbaine
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {keyStats.urbanPercentage}%
                              </Typography>
                              <Typography variant="caption">
                                {ageGroupData.total.urbain.total} personnes
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Population Rurale
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {keyStats.ruralPercentage}%
                              </Typography>
                              <Typography variant="caption">
                                {ageGroupData.total.rural.total} personnes
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Groupe le plus important
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.largestAgeGroup.age}
                              </Typography>
                              <Typography variant="body1">
                                {keyStats.largestAgeGroup.total}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
                  <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
                    <Table size="small" aria-label="Tableau des données démographiques">
                      <StyledTableHead>
                        <TableRow>
                          <TableCell rowSpan={3}>Groupe d'âge</TableCell>
                           <TableCell align="center">Burundi</TableCell>
                          <TableCell align="center" colSpan={3}>Urbain</TableCell>
                          <TableCell align="center" colSpan={3}>Rural</TableCell>
                        </TableRow>
                        <TableRow>
                           <TableCell align="center">Total</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Masculin</TableCell>
                          <TableCell align="right">Féminin</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Masculin</TableCell>
                          <TableCell align="right">Féminin</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {/* Données par groupe d'âge */}
                        {filteredAgeGroups.map((group, index) => (
                          <AgeRow key={index} hover>
                            <TableCell component="th" scope="row">{group.age}</TableCell>
                             <TableCell align="right">{group.total}</TableCell>
                            <TableCell align="right">{group.urbain.total}</TableCell>
                            <TableCell align="right">{group.urbain.masculin}</TableCell>
                            <TableCell align="right">{group.urbain.feminin}</TableCell>
                            <TableCell align="right">{group.rural.total}</TableCell>
                            <TableCell align="right">{group.rural.masculin}</TableCell>
                            <TableCell align="right">{group.rural.feminin}</TableCell>
                          </AgeRow>
                        ))}
                        
                        {/* Ligne pour le total */}
                        <AgeRow hover>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.total}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.urbain.total}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.urbain.masculin}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.urbain.feminin}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.rural.total}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.rural.masculin}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{ageGroupData.total.rural.feminin}</TableCell>
                        </AgeRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                
                {/* Section d'information */}
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="primary">
                      À propos des groupes d'âges fonctionnels
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" paragraph>
                      Les groupes d'âges fonctionnels sont définis selon les besoins spécifiques en matière de santé publique:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      <ListItem>
                        <ListItemText 
                          primary="• < 1 an: Nourrissons nécessitant des soins spécifiques" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• < 5 ans: Enfants en bas âge (vaccination, nutrition)" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• 10-19 ans: Adolescents (santé reproductive, éducation)" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• 15-34 ans: Jeunes adultes (emploi, santé mentale)" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• 60 ans et plus: Personnes âgées (maladies chroniques)" 
                        />
                      </ListItem>
                    </List>
                    <Typography variant="body2" mt={2}>
                      Ces données montrent que {keyStats.ruralPercentage}% de la population vit en milieu rural, 
                      avec des disparités selon les groupes d'âge et le sexe.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                {/* Notes méthodologiques */}
                <Paper sx={{ p: 2, backgroundColor: '#f0f0f0', borderLeft: `4px solid ${secondaryColor}` }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Notes méthodologiques
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les données présentées sont issues du Recensement Général de la Population, de l'Habitat, 
                    de l'Agriculture et de l'Elevage (RGPHAE) réalisé en 2024. Les groupes d'âges ont été 
                    définis selon les standards internationaux en matière de santé publique. Les proportions 
                    sont calculées par rapport à la population totale des ménages ordinaires.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
      </MainContentWithDrawer>
      
      {/* Pied de page */}
      <Footer />
    </MainContainer>
  );
};

export default AgeGroupPopulationPage;